import React from "react";
import { readInlineData, usePaginationFragment } from "react-relay";
import { type DataTableSelectionChangeParams } from "@components/data-table";
import { SearchableTable } from "@components/searchable-table";
import { SelectComplexDataField } from "@components/select-complex-data-field";
import { useTranslation } from "@i18n/hooks";
import {
	type selectAcademiesTagField_AcademiesTagFragment$data,
	type selectAcademiesTagField_AcademiesTagFragment$key,
} from "@relay/selectAcademiesTagField_AcademiesTagFragment.graphql";
import { type selectAcademiesTagField_QueryFragment$key } from "@relay/selectAcademiesTagField_QueryFragment.graphql";
import { type selectAcademiesTagField_Refetch } from "@relay/selectAcademiesTagField_Refetch.graphql";
import { ACADEMIES_TAG_FRAGMENT, QUERY } from "./select-academies-tag-field.graphql";
import {
	type SelectAcademiesTagFieldProps,
	type SelectAcademiesTagFieldState,
} from "./select-academies-tag-field.types";
import { toSingleSelectionValue } from "./select-academies-tag-field.util";

export const SelectAcademiesTagField = ({
	queryFragmentRef,
	singleSelection = false,
	...props
}: SelectAcademiesTagFieldProps) => {
	const { t } = useTranslation("common");
	const {
		data: {
			Admin: {
				AcademiesTag: { SelectTags },
			},
		},
		hasPrevious,
		hasNext,
		refetch,
		loadPrevious,
		loadNext,
	} = usePaginationFragment<
		selectAcademiesTagField_Refetch,
		selectAcademiesTagField_QueryFragment$key
	>(QUERY, queryFragmentRef);

	const handleLoadPrevious = () => {
		loadPrevious(20);
	};

	const handleLoadNext = () => {
		loadNext(20);
	};

	const handleOnSearch = (search: string) => {
		refetch({ name: search });
	};

	const handleOnMultipleSelectionChange = (
		event: DataTableSelectionChangeParams<selectAcademiesTagField_AcademiesTagFragment$data[]>,
	) => {
		props.updateField(event.value);
	};

	const handleOnSingleSelectionChange = (
		event: DataTableSelectionChangeParams<selectAcademiesTagField_AcademiesTagFragment$data>,
	) => {
		props.updateField(toSingleSelectionValue(event.value));
	};

	const values =
		SelectTags.edges
			?.filter((e) => !!e?.node)
			.map((e) =>
				readInlineData<selectAcademiesTagField_AcademiesTagFragment$key>(
					ACADEMIES_TAG_FRAGMENT,
					e!.node,
				),
			) || [];
	const commonTableProps = {
		dataKey: "id" as const,
		hasPrevious,
		metaKeySelection: false,
		hasNext,
		value: values,
		loadPrevious: handleLoadPrevious,
		loadNext: handleLoadNext,
		onSearch: handleOnSearch,
	};

	return (
		<SelectComplexDataField<SelectAcademiesTagFieldState>
			valuePlaceholder={t("tags.noTagsSelected")}
			actionButtonLabel={singleSelection ? t("tags.selectTag") : t("tags.selectTags")}
			headerLabel={singleSelection ? t("tags.selectTagLabel") : t("tags.selectTagsLabel")}
			valueBody={
				singleSelection
					? (data) => <>{data?.[0]?.data.name ?? t("tags.noTagsSelected")}</>
					: undefined
			}
			{...props}
		>
			{singleSelection ? (
				<SearchableTable
					{...commonTableProps}
					selectionMode="single"
					selection={props.fieldValue?.[0]}
					onSelectionChange={handleOnSingleSelectionChange}
				>
					{(Column) => <Column header={t("table.name")} body={(e) => e.data.name} />}
				</SearchableTable>
			) : (
				<SearchableTable
					{...commonTableProps}
					selectionMode="multiple"
					selection={props.fieldValue}
					onSelectionChange={handleOnMultipleSelectionChange}
				>
					{(Column) => <Column header={t("table.name")} body={(e) => e.data.name} />}
				</SearchableTable>
			)}
		</SelectComplexDataField>
	);
};
